// Voice answer fixes: keep unsent text in the answer boxes (weekly questions,
// Request changes, client and agency plan notes) across reloads and closed tabs.
//
//   const clearDraft = useDraft(key, value, setValue, onRestore);
//
// key       'page.field.<job id>' (null while the page is still loading: nothing
//           is read, written or warned about until there is a key)
// value     the box's text; setValue its setter
// onRestore optional, called when saved text was put back (e.g. reopen a modal)
//
// On a new key the saved text is restored into an empty box. Every change is
// saved as localStorage lotus.draft.<key> (an empty box removes it), and while
// there is unsent text the browser asks before the tab closes or reloads.
// Call clearDraft() after a successful submit: it drops the saved copy and
// stops the warning until the text changes again. Storage failures (private
// mode, quota) are ignored; the box keeps working without persistence.

import { useCallback, useEffect, useRef, useState } from 'react';

const PREFIX = 'lotus.draft.';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;   // drop drafts older than 30 days

function readDraft(key) {
  try {
    const raw = window.localStorage.getItem(PREFIX + key);
    if (!raw) return '';
    const d = JSON.parse(raw);
    if (!d || typeof d.text !== 'string' || !(Date.now() - Number(d.at) < MAX_AGE_MS)) {
      window.localStorage.removeItem(PREFIX + key);
      return '';
    }
    return d.text;
  } catch { return ''; }
}

function writeDraft(key, text) {
  try {
    if (text.trim()) window.localStorage.setItem(PREFIX + key, JSON.stringify({ text, at: Date.now() }));
    else window.localStorage.removeItem(PREFIX + key);
  } catch { /* private mode, quota etc. */ }
}

export function useDraft(key, value, setValue, onRestore) {
  const [readyKey, setReadyKey] = useState(null);  // key whose saved text has been restored
  const [sent, setSent] = useState(null);          // text handed to clearDraft()
  const valueRef = useRef(value);
  valueRef.current = value;

  // Restore once per key, into an empty box only.
  useEffect(() => {
    if (!key) { setReadyKey(null); return; }
    const saved = readDraft(key);
    if (saved && !String(valueRef.current || '').trim()) {
      setValue(saved);
      if (onRestore) onRestore();
    }
    setSent(null);
    setReadyKey(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Save on change, but not before the restore above has landed.
  const text = String(value || '');
  useEffect(() => {
    if (!key || readyKey !== key || text === sent) return;
    if (sent !== null) setSent(null);
    writeDraft(key, text);
  }, [key, readyKey, text, sent]);

  const unsent = !!key && !!text.trim() && text !== sent;
  useEffect(() => {
    if (!unsent) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [unsent]);

  return useCallback(() => {
    if (key) writeDraft(key, '');
    setSent(String(valueRef.current || ''));
  }, [key]);
}
